import { GrowthBook } from '@growthbook/growthbook-react';

import Config from '@/config';

// Extracted from App.tsx so modules that need `gb` (e.g. util/date.ts) don't
// have to import App itself, which would create an App -> ... -> App cycle.
export const gb = new GrowthBook({
  apiHost: Config.GROWTHBOOK_API_HOST,
  clientKey: Config.GROWTHBOOK_CLIENT_KEY,
});

// Whether this build has GrowthBook credentials at all.
//
// A bundle built without them defines these as `undefined` (webpack rewrites an
// unset `process.env` to the literal "MISSING_ENV_VAR", and a property read on
// that string is undefined). The SDK then never fetches a feature payload and
// every `useFeatureValue(key, false)` silently answers with its default — not
// "flag off by policy", but "flag unknowable".
//
// That is the state of every REVIEW APP: they serve the baseline admin bundle,
// which is built with no GrowthBook keys. It is why the whole of GRAVY-10443's
// UI read as "not built" during QA — the code shipped, the gates could not
// resolve (GRAVY-10848).
export const growthbookConfigured = Boolean(
  Config.GROWTHBOOK_CLIENT_KEY && Config.GROWTHBOOK_API_HOST,
);

/**
 * Resolve GrowthBook keys from graphy's OWN feature-flag table when GrowthBook
 * is unreachable, so an environment without GrowthBook credentials is still
 * configurable instead of being uniformly off.
 *
 * Deliberately a no-op wherever GrowthBook IS configured: QA, UAT and
 * production keep resolving exactly as before, and this cannot become a second
 * source of truth competing with GrowthBook in those environments. It only
 * fills a vacuum.
 *
 * This is NOT a route for new flags. New gates belong on the internal platform
 * outright — `FeatureFlags::EvaluationService.global_enabled?` on the server,
 * `useFeatureFlag` on the client (see PayEscalation::FLAG_KEY). This exists so
 * the flags that were already on GrowthBook before the platform landed remain
 * testable on a review app.
 *
 * A variant of 'off' is false; any other variant (an arm name, 'on') is true.
 * Every call site of these keys reads them as booleans.
 */
export function applyInternalFlagFallback(map: Record<string, string>): void {
  if (growthbookConfigured) return;

  const forced = new Map<string, unknown>();
  Object.entries(map).forEach(([key, variant]) =>
    forced.set(key, variant !== 'off'),
  );
  gb.setForcedFeatures(forced);
}
